import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api';

export default function ExpiryCommandCenter({ user }) {
    const [activeTab, setActiveTab] = useState('Drivers');
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);

    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : null);
    const isSuperadmin = user?.userType === 'admin';

    useEffect(() => {
        if (!operatorId && !isSuperadmin) return;
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                const drvUrl = isSuperadmin ? `/api/drivers/all` : `/api/drivers/${operatorId}`;
                const vehUrl = isSuperadmin ? `/api/vehicles/${operatorId === null ? 'all' : operatorId}` : `/api/vehicles/${operatorId}`;
                
                const [drvRes, vehRes] = await Promise.all([
                    axios.get(drvUrl).catch(() => ({ data: [] })),
                    axios.get(vehUrl).catch(() => ({ data: [] }))
                ]);
                
                setDrivers(Array.isArray(drvRes.data) ? drvRes.data : []);
                setVehicles(Array.isArray(vehRes.data) ? vehRes.data : []);
            } catch (err) {
                console.error('Error fetching expiry data:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, [operatorId, isSuperadmin]);

    const d = new Date();
    const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    // Compute expired lists strictly
    const expiredDriversList = drivers.filter(driver => {
        let isExp = false;
        if (driver.civilIdExpiry && String(driver.civilIdExpiry).split('T')[0] < todayStr) isExp = true;
        if (driver.passportExpiry && String(driver.passportExpiry).split('T')[0] < todayStr) isExp = true;
        if (driver.licenseExpiry && String(driver.licenseExpiry).split('T')[0] < todayStr) isExp = true;
        return isExp;
    });

    const expiredVehiclesList = vehicles.filter(vehicle => {
        let isExp = false;
        if (vehicle.insuranceExpiry && String(vehicle.insuranceExpiry).split('T')[0] < todayStr) isExp = true;
        if (vehicle.registrationExpiry && String(vehicle.registrationExpiry).split('T')[0] < todayStr) isExp = true;
        if (vehicle.vehicleLicenseExpiry && String(vehicle.vehicleLicenseExpiry).split('T')[0] < todayStr) isExp = true;
        return isExp;
    });

    const tabs = [
        { name: 'Drivers', count: expiredDriversList.length, icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z' },
        { name: 'Fleet & Vehicle', count: expiredVehiclesList.length, icon: 'M7 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0 M17 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0 M5 17H3V7c0-1.1.9-2 2-2h11v12h-2 M19 17h2v-6l-3-4h-2v7h4' }
    ];

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        if (isNaN(date)) return dateStr;
        return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
    };

    const isExpired = (dateStr) => {
        if (!dateStr) return false;
        return String(dateStr).split('T')[0] < todayStr;
    };

    const renderDateCell = (dateStr) => {
        if (!dateStr) return <span>-</span>;
        const exp = isExpired(dateStr);
        return <span style={{ color: exp ? '#ef4444' : '#64748b', fontWeight: exp ? 'bold' : 'normal' }}>{formatDate(dateStr)}</span>;
    };

    return (
        <div style={{ background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>
            <div style={{ padding: '24px 32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                    <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#ef4444', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8 }}>Compliance & Monitoring</div>
                        <h1 style={{ fontSize: 32, fontWeight: 900, color: '#0f172a', margin: 0, letterSpacing: '-0.03em', lineHeight: 1.1 }}>Expiry Command Center</h1>
                        <p style={{ color: '#64748b', fontSize: 14, margin: '10px 0 0', fontWeight: 500 }}>Filtered monitoring for strictly expired records across the system.</p>
                    </div>
                    <button style={{ 
                        display: 'flex', alignItems: 'center', gap: 8, background: 'white', border: '1px solid #e2e8f0', 
                        padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#0f172a', cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                        Send Email Report
                    </button>
                </div>

                <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', background: 'white', borderRadius: 12, padding: '8px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        {tabs.map(tab => {
                            const active = activeTab === tab.name;
                            return (
                                <div 
                                    key={tab.name}
                                    onClick={() => setActiveTab(tab.name)}
                                    style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                        padding: '12px 24px', borderRadius: 8, cursor: 'pointer', transition: 'all 0.2s',
                                        background: active ? '#111827' : 'transparent',
                                        color: active ? 'white' : '#64748b',
                                        minWidth: 110, gap: 6
                                    }}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        {tab.icon.split(' M').map((p, i) => <path key={i} d={i === 0 ? p : 'M' + p} />)}
                                    </svg>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700 }}>
                                        {tab.name}
                                        <span style={{ 
                                            background: active ? 'rgba(255,255,255,0.2)' : '#f1f5f9', 
                                            color: active ? 'white' : tab.count > 0 ? '#ef4444' : '#94a3b8',
                                            padding: '2px 6px', borderRadius: 12, fontSize: 10, fontWeight: 800
                                        }}>
                                            {tab.count}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.03)', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                                {activeTab === 'Drivers' && (
                                    <>
                                        <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em' }}>DRIVER ID <span style={{fontSize:9}}>▼</span></th>
                                        <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em' }}>CONTACT <span style={{fontSize:9}}>▼</span></th>
                                        <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em' }}>CIVIL ID EXPIRY <span style={{fontSize:9}}>▼</span></th>
                                        <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em' }}>PASSPORT EXPIRY <span style={{fontSize:9}}>▼</span></th>
                                        <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em' }}>LICENSE EXPIRY <span style={{fontSize:9}}>▼</span></th>
                                    </>
                                )}
                                {activeTab === 'Fleet & Vehicle' && (
                                    <>
                                        <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em' }}>VEHICLE ID / PLATE <span style={{fontSize:9}}>▼</span></th>
                                        <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em' }}>MAKE & MODEL <span style={{fontSize:9}}>▼</span></th>
                                        <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em' }}>INSURANCE EXPIRY <span style={{fontSize:9}}>▼</span></th>
                                        <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em' }}>REGISTRATION EXPIRY <span style={{fontSize:9}}>▼</span></th>
                                        <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em' }}>LICENSE EXPIRY <span style={{fontSize:9}}>▼</span></th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan="5" style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 14 }}>Loading...</td>
                                </tr>
                            )}
                            {!loading && activeTab === 'Drivers' && expiredDriversList.map(driver => (
                                <tr key={driver._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '16px 24px', fontSize: 13, fontWeight: 700, color: '#3b82f6' }}>{driver.driverCode || driver.driverId || driver.name}</td>
                                    <td style={{ padding: '16px 24px', fontSize: 13, color: '#0f172a', fontWeight: 500 }}>{driver.phone || driver.email || '-'}</td>
                                    <td style={{ padding: '16px 24px', fontSize: 13 }}>{renderDateCell(driver.civilIdExpiry)}</td>
                                    <td style={{ padding: '16px 24px', fontSize: 13 }}>{renderDateCell(driver.passportExpiry)}</td>
                                    <td style={{ padding: '16px 24px', fontSize: 13 }}>{renderDateCell(driver.licenseExpiry)}</td>
                                </tr>
                            ))}
                            {!loading && activeTab === 'Fleet & Vehicle' && expiredVehiclesList.map(vehicle => (
                                <tr key={vehicle._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '16px 24px', fontSize: 13, fontWeight: 700, color: '#3b82f6' }}>{vehicle.vehicleCode || vehicle.plateNumber}</td>
                                    <td style={{ padding: '16px 24px', fontSize: 13, color: '#0f172a', fontWeight: 500 }}>{vehicle.make} {vehicle.model}</td>
                                    <td style={{ padding: '16px 24px', fontSize: 13 }}>{renderDateCell(vehicle.insuranceExpiry)}</td>
                                    <td style={{ padding: '16px 24px', fontSize: 13 }}>{renderDateCell(vehicle.registrationExpiry)}</td>
                                    <td style={{ padding: '16px 24px', fontSize: 13 }}>{renderDateCell(vehicle.vehicleLicenseExpiry)}</td>
                                </tr>
                            ))}
                            {!loading && activeTab !== 'Drivers' && activeTab !== 'Fleet & Vehicle' && (
                                <tr>
                                    <td colSpan="5" style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>No data for {activeTab} yet.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    <div style={{ padding: '12px 24px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <button style={{ padding: '6px 12px', background: 'white', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#64748b' }}>&lt;</button>
                            <button style={{ padding: '6px 12px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#0f172a' }}>1</button>
                            <button style={{ padding: '6px 12px', background: 'white', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#64748b' }}>&gt;</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
